import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/database.types';
import { toast } from 'react-hot-toast';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Add better error handling for missing environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables:', {
    url: supabaseUrl ? 'present' : 'missing',
    key: supabaseAnonKey ? 'present' : 'missing'
  });
  throw new Error('Las variables de entorno de Supabase no están configuradas correctamente. Por favor, verifica tu archivo .env');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage,
    flowType: 'pkce'
  },
  global: {
    headers: {
      'X-Client-Info': 'skymenu-web'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Mejorar el manejo de errores de autenticación
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    localStorage.removeItem('supabase.auth.token');
  } else if (event === 'TOKEN_REFRESHED') {
    if (session) {
      localStorage.setItem('supabase.auth.token', session.access_token);
    }
  }
});

// Mejorar la función de verificación de conexión con más detalles de error
export async function checkConnection(retries = 3, delay = 1000) {
  for (let i = 0; i < retries; i++) {
    try {
      const { error } = await supabase.from('products').select('count').limit(1).single();
      if (error) {
        if (error.code === 'PGRST301') {
          console.error('Error de autenticación JWT:', error);
          // Invalid JWT token
          const { error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('Error al refrescar la sesión:', refreshError);
            throw refreshError;
          }
          // Try the connection again after refresh
          const { error: retryError } = await supabase.from('products').select('count').limit(1).single();
          if (retryError) {
            console.error('Error después de refrescar el token:', retryError);
            throw retryError;
          }
        } else {
          console.error('Error de Supabase:', error);
          throw error;
        }
      }
      return true;
    } catch (error: any) {
      console.error(`Intento ${i + 1} de conexión fallido:`, error);

      const isNetworkError = error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError') ||
        error.message?.includes('network') ||
        !window.navigator.onLine;

      // Si es el último intento, lanzar el error
      if (i === retries - 1) {
        if (isNetworkError) {
          throw new Error('No se pudo conectar al servidor de Supabase. Por favor, verifica tu conexión a internet.');
        }

        if (error.code === 'PGRST301' || error.message?.includes('JWT')) {
          throw new Error('Error de autenticación. Por favor, inicia sesión nuevamente.');
        }

        throw new Error(`Error de conexión con Supabase: ${error.message || 'Error desconocido'}`);
      }

      // Si es error de red, esperar y reintentar
      if (isNetworkError) {
        console.log(`Esperando ${delay}ms para reintentar conexión...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }

      // Si es otro tipo de error, lanzar inmediatamente
      throw error;
    }
  }
  return false;
}

// Mejorar la función de reintento con más información de depuración
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  delay = 1000
): Promise<T> {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;
      console.error(`Intento ${i + 1} fallido:`, error);

      // Si es un error de autenticación, intentar refrescar el token
      if (error?.status === 401 || error?.message?.includes('JWT')) {
        try {
          console.log('Intentando refrescar el token de autenticación...');
          const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
          if (refreshError) {
            console.error('Error al refrescar la sesión:', refreshError);
            throw refreshError;
          }
          if (!session) {
            console.error('No se pudo obtener una nueva sesión');
            throw new Error('No hay sesión activa');
          }

          console.log('Token refrescado exitosamente');
          // Esperar un momento antes de reintentar
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        } catch (authError) {
          console.error('Error fatal de autenticación:', authError);
          // Si no se puede refrescar el token, redirigir al login
          window.location.href = '/login';
          break;
        }
      }

      if (i < maxRetries - 1) {
        console.log(`Esperando ${delay}ms antes del siguiente intento...`);
        await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }

  console.error('Se agotaron los reintentos. Último error:', lastError);
  throw lastError;
}